// frontend/src/App.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { UserProvider, useUser } from './contexts/UserContext';
import { NotificationProvider, useNotification } from './contexts/NotificationContext';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import { LanguageProvider, useLanguage } from './contexts/LanguageContext';

import { getChatSessions, registerSessionExpiredHandler } from './api';

import SessionManager from './components/SessionManager';
import SideBar from './components/SideBar';
import ConsultationManager from './components/ConsultationManager';
import Login from './components/Login';
import Chat from './components/Chat';
import AcademicManager from './components/AcademicManager';
import LandingPage from './components/LandingPage';
import Register from './components/Register';
import DracmaPurchase from './components/DracmaPurchase';
import Profile from './components/Profile';
import DNALoadingAnimation from './components/DNALoadingAnimation';
import TestDNALoading from './components/TestDNALoading';
import CustomNotification from './components/CustomNotification';
import './global.css';

const API_URL = process.env.REACT_APP_API_URL || 'https://quiron.app/api';

const ProtectedRoute = ({ isLoggedIn, initialCheckComplete, children }) => {
if (!initialCheckComplete) {
return <DNALoadingAnimation />;
}
return isLoggedIn ? children : <Navigate to="/login" replace />;
};

const AppLayoutAndRoutes = () => {
const { user, setUser, isLoading: isUserLoading } = useUser();
const { setTheme } = useTheme();
const { changeLanguage } = useLanguage();
const { addNotification } = useNotification();

const [isSidebarOpen, setIsSidebarOpen] = useState(false);
const [isLoggedIn, setIsLoggedIn] = useState(false);
const [initialCheckComplete, setInitialCheckComplete] = useState(false);

const [chatSessions, setChatSessions] = useState([]);
const [activeChatId, setActiveChatId] = useState(null);

const fetchChatSessions = useCallback(async () => {
if (isLoggedIn) {
try {
const sessions = await getChatSessions();
setChatSessions(sessions.map(s => ({ ...s, isNew: false })));
} catch (error) {
console.error("Failed to fetch chat sessions:", error);
}
}
}, [isLoggedIn]);

const handleSessionAnimationEnd = (sessionId) => {
setChatSessions(prevSessions =>
prevSessions.map(session =>
session.id === sessionId ? { ...session, isNew: false } : session
)
);
};

const handleNewChatCreated = (newId, title) => {
setChatSessions(prevSessions => [{ id: newId, title: title, isNew: true }, ...prevSessions]);
setActiveChatId(newId);
};

const handleTitleUpdate = (sessionId, newTitle) => {
setChatSessions(prevSessions =>
prevSessions.map(session =>
session.id === sessionId ? { ...session, title: newTitle, isNew: false } : session
)
);
};

const navigate = useNavigate();

const handleLogout = useCallback(() => {
    localStorage.removeItem('authToken');
    setUser(null);
    setIsLoggedIn(false);
    setChatSessions([]);
    setActiveChatId(null);
    addNotification('Sua sessão expirou. Por favor, faça o login novamente.', 'info');
    navigate('/login');
}, [setUser, setIsLoggedIn, navigate, addNotification]);

useEffect(() => {
    registerSessionExpiredHandler(handleLogout);
}, [handleLogout]);


useEffect(() => {
fetchChatSessions();
}, [fetchChatSessions]);

const location = useLocation();
const sidebarRef = useRef(null);
const contentRef = useRef(null);

const workspaceRoutes = ['/consultation-manager', '/neuralweb', '/academic', '/profile'];

const handleInitialCheckComplete = useCallback(() => {
setInitialCheckComplete(true);
}, []);

const toggleSidebar = () => {
setIsSidebarOpen(!isSidebarOpen);
};

const sidebarOpenWidth = 220;
const sidebarClosedWidth = 80;

useEffect(() => {
if (!isUserLoading && user) {
if (user.theme_preference) {
setTheme(user.theme_preference);
}
if (user.language_preference) {
changeLanguage(user.language_preference);
}
}
}, [user, isUserLoading, setTheme, changeLanguage]);

return (
<>
<SessionManager setIsLoggedIn={setIsLoggedIn} onInitialCheckComplete={handleInitialCheckComplete} />
<div className="app-container dark-theme">
  {isLoggedIn && workspaceRoutes.includes(location.pathname) && (
      <>
        <div
          ref={sidebarRef}
          className={`sidebar-app-container ${isSidebarOpen ? 'open' : 'closed'}`}
          onMouseEnter={() => setIsSidebarOpen(true)}
          onMouseLeave={() => setIsSidebarOpen(false)}
        >
          <SideBar onLogout={handleLogout} isOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />
        </div>
      </>
    )}
    <div
      ref={contentRef}
      className="main-content-area"
    >
      <div style={{ minHeight: location.pathname === '/' ? '300vh' : 'auto' }}>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<Login setIsLoggedIn={setIsLoggedIn} />} />
          <Route path="/register" element={<Register />} />
          <Route path="/test-dna" element={<TestDNALoading />} />
          <Route
            path="/consultation-manager"
            element={
              <ProtectedRoute isLoggedIn={isLoggedIn} initialCheckComplete={initialCheckComplete}>
                <ConsultationManager isSidebarOpen={isSidebarOpen} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/neuralweb"
            element={
              <ProtectedRoute isLoggedIn={isLoggedIn} initialCheckComplete={initialCheckComplete}>
                <Chat
                  isSidebarOpen={isSidebarOpen}
                  sidebarWidth={isSidebarOpen ? sidebarOpenWidth : sidebarClosedWidth}
                  sessions={chatSessions}
                  activeChatId={activeChatId}
                  onSelectChat={setActiveChatId}
                  onNewChat={() => setActiveChatId(null)}
                  onNewChatCreated={handleNewChatCreated}
                  onTitleUpdate={handleTitleUpdate}
                  onSessionAnimationEnd={handleSessionAnimationEnd}
                  refreshSessions={fetchChatSessions}
                />
              </ProtectedRoute>
            }
          />
          <Route
            path="/academic"
            element={
              <ProtectedRoute isLoggedIn={isLoggedIn} initialCheckComplete={initialCheckComplete}>
                <AcademicManager isSidebarOpen={isSidebarOpen} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dracma-purchase"
            element={
              <ProtectedRoute isLoggedIn={isLoggedIn} initialCheckComplete={initialCheckComplete}>
                <DracmaPurchase />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <ProtectedRoute isLoggedIn={isLoggedIn} initialCheckComplete={initialCheckComplete}>
                <Profile isSidebarOpen={isSidebarOpen} sidebarWidth={isSidebarOpen ? sidebarOpenWidth : sidebarClosedWidth} />
              </ProtectedRoute>
            }
          />
        </Routes>
        <div id="result-portal"></div>
      </div>
    </div>
  </div>
</>
);
};

const AppWithProviders = () => (
<UserProvider>
<LanguageProvider>
<ThemeProvider>
<NotificationProvider>
<AppLayoutAndRoutes />
</NotificationProvider>
</ThemeProvider>
</LanguageProvider>
</UserProvider>
);

function App() {
return (
<AppWithProviders />
);
}

export default App;